import React, { useState, useEffect } from 'react';
import { Button } from '../Button/Button';
import { CloseIcon, LinkIcon, SearchIcon, EnableCompanyIcon } from './icons';
import type { MasterCrudCompany, MasterCrudField } from './MasterCrud.types';

interface LinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  recordId: string | number;
  record: any;
  companies: MasterCrudCompany[];
  fields: MasterCrudField[];
  onConfirm: (recordId: string | number, companyUuids: (string | number)[]) => void;
  isLinked: (companyUuid: string | number) => boolean;
  variant?: 'success' | 'manual';
  /** Función de traducción inyectada por el padre */
  t: (key: string, defaultValue?: string) => string;
}

/**
 * LinkModal - Modal para vinculación de registros a múltiples compañías.
 */
export const LinkModal: React.FC<LinkModalProps> = ({
  isOpen,
  onClose,
  recordId,
  record,
  companies,
  fields,
  onConfirm,
  isLinked,
  variant = 'manual',
  t,
}) => {
  // ===== ESTADO LOCAL DE SELECCIÓN =====
  const [selectedUuids, setSelectedUuids] = useState<(string | number)[]>([]);
  const [initialUuids, setInitialUuids] = useState<(string | number)[]>([]);
  // Búsqueda frontend por nombre de compañía
  const [search, setSearch] = useState('');

  const targetCompanies = companies.filter(c => c.uuid !== '000');

  // El buscador solo aparece cuando la lista es suficientemente larga para justificarlo.
  const showSearch = targetCompanies.length > 3;

  const normalizedSearch = search.trim().toLowerCase();
  const filteredCompanies = normalizedSearch
    ? targetCompanies.filter(c => (c.name || '').toLowerCase().includes(normalizedSearch))
    : targetCompanies;

  // Inicializar estado con las vinculaciones actuales al abrir
  useEffect(() => {
    if (isOpen) {
      const initialLinks = targetCompanies
        .filter(c => isLinked(c.uuid))
        .map(c => c.uuid);
      setSelectedUuids(initialLinks);
      setInitialUuids(initialLinks);
      setSearch('');
    }
  }, [isOpen, recordId]);

  if (!isOpen) return null;

  // ===== COMPARACIÓN DE CAMBIOS =====
  const hasChanges = JSON.stringify([...selectedUuids].sort()) !== JSON.stringify([...initialUuids].sort());

  // ===== MANEJADORES LOCALES =====
  const handleToggleLink = (uuid: string | number) => {
    setSelectedUuids(prev => 
      prev.includes(uuid) 
        ? prev.filter(c => c !== uuid) 
        : [...prev, uuid]
    );
  };

  const handleLinkAll = () => {
    setSelectedUuids(targetCompanies.map(c => c.uuid));
  };

  const handleConfirm = () => {
    onConfirm(recordId, selectedUuids);
    onClose();
  };

  // Construir título dinámico para el modo manual
  const getDynamicTitle = () => {
    if (variant === 'success') return t('recordCreatedSuccess', 'Haz creado el registro exitosamente');
    
    const titleValues = fields
      .filter(f => f.config?.showInLinkModalTitle)
      .map(f => {
        const val = record[f.accessorKey as string];
        return val ? String(val) : '';
      })
      .filter(v => v !== '');

    const suffix = titleValues.length > 0 ? ` · ${titleValues.join(' - ')}` : '';
    return `${t('recordLinking', 'Habilitar registro')}${suffix}`;
  };

  const getDescription = () => {
    if (variant === 'success') {
      return t('globalRecordSaved', 'El registro se guardó de forma Global; ahora puedes asignarlo a las compañías de tu elección');
    }
    return t('assignRecordToCompanies', 'Puedes asignar este registro a las compañías de tu elección');
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-xl shadow-2xl flex flex-col w-full max-w-2xl overflow-hidden relative">
        
        {/* Botón Cerrar (X) */}
        <div className="absolute right-4 top-4 z-10">
          <Button 
            type="outline" 
            size="xs" 
            iconOnly 
            leftIcon={<CloseIcon className="w-4 h-4" />} 
            onClick={onClose} 
            ariaLabel="Cerrar" 
            className="!bg-white dark:!bg-dark-bg-primary"
          />
        </div>

        {/* Header — icono azul de marca (no verde/lima) */}
        <div className="p-[20px] flex gap-5 items-center">
          <div className="bg-[#dbeefe] dark:bg-primary-900/20 flex items-center justify-center rounded-full shrink-0 size-[40px]">
            <LinkIcon className="size-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex flex-col justify-center">
            <h3 className="text-[20px] font-bold leading-[28px] tracking-tight text-content-primary dark:text-dark-content-primary">
              {getDynamicTitle()}
            </h3>
            <p className="text-[14px] font-normal italic leading-[20px] text-content-tertiary dark:text-content-tertiary">
              {getDescription()}
            </p>
          </div>
        </div>

        {/* Buscador frontend (fijo, no scrollea con la lista) */}
        {showSearch && (
          <div className="px-[20px] pb-[12px]">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('searchCompanyPlaceholder', 'Buscar compañía…')}
                aria-label={t('searchCompanyPlaceholder', 'Buscar compañía…')}
                autoComplete="off"
                className="w-full h-10 pl-9 pr-9 rounded-lg border border-border-primary dark:border-dark-border-primary bg-white dark:bg-dark-bg-primary text-sm text-content-primary dark:text-dark-content-primary placeholder:text-content-tertiary focus:outline-none focus:border-[#93d1fd] focus:ring-[3px] focus:ring-primary-500/10 transition-colors"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label={t('clear', 'Limpiar')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-6 h-6 rounded-md text-content-tertiary hover:text-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors"
                >
                  <CloseIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Body - Lista de compañías */}
        <div className="px-[20px] pb-[20px] flex flex-col gap-[20px] overflow-y-auto max-h-[400px]">
          <div className="flex flex-col items-start w-full">
            {filteredCompanies.map((company) => {
              const isCurrentlyLinked = selectedUuids.includes(company.uuid);
              return (
                <div
                  key={company.uuid}
                  className="border border-border-primary dark:border-dark-border-primary h-[64px] px-[8px] flex items-center justify-between rounded-[8px] w-full mb-3 last:mb-0 transition-colors hover:border-[#93d1fd] hover:bg-[#eff8ff]/40 dark:hover:bg-primary-900/10"
                >
                  <div className="flex items-center gap-[8px]">
                    <div className="bg-[#dbeefe] dark:bg-primary-900/20 flex items-center justify-center rounded-[16px] shrink-0 size-[32px]">
                      <EnableCompanyIcon className="w-[18px] h-[18px]" />
                    </div>
                    <span className="text-[16px] font-medium leading-[24px] text-content-primary dark:text-dark-content-primary truncate max-w-[300px]">
                      {company.name}
                    </span>
                  </div>

                  {isCurrentlyLinked ? (
                    <button
                      type="button"
                      onClick={() => handleToggleLink(company.uuid)}
                      className="inline-flex items-center h-8 px-3 rounded-md text-xs font-semibold border transition-all bg-error/10 text-error-content border-error-border hover:brightness-95 dark:bg-error/20"
                    >
                      {t('unlink', 'Deshabilitar')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleToggleLink(company.uuid)}
                      className="inline-flex items-center h-8 px-3 rounded-md text-xs font-semibold border transition-all bg-white dark:bg-dark-bg-primary text-primary-600 border-[#93d1fd] hover:bg-[#eff8ff] dark:hover:bg-primary-900/20"
                    >
                      {t('link', 'Habilitar')}
                    </button>
                  )}
                </div>
              );
            })}

            {/* Estado vacío del buscador */}
            {filteredCompanies.length === 0 && (
              <div className="w-full flex flex-col items-center justify-center gap-2 py-10 text-center">
                <SearchIcon className="w-6 h-6 text-content-tertiary" />
                <p className="text-sm text-content-secondary dark:text-dark-content-secondary">
                  {t('noCompaniesFound', 'No se encontraron compañías')}
                </p>
                <p className="text-xs text-content-tertiary">
                  <span className="font-medium text-content-primary dark:text-dark-content-primary">“{search}”</span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer — botones h-9 px-3 (tamaño del listado, S2.2) */}
        <div className="px-[24px] py-[16px] pb-[20px] flex items-center justify-end gap-3 shrink-0 w-full border-t border-border-secondary dark:border-dark-border-primary">
          <Button type="plain" size="base" className="!h-9 !px-3 !text-sm font-medium" onClick={onClose}>
            {t('cancelText', 'Cancelar')}
          </Button>
          <Button type="outline" size="base" className="!h-9 !px-3 !text-sm font-medium" onClick={handleLinkAll}>
            {t('linkAll', 'Habilitar todas')}
          </Button>
          <Button type="default" size="base" className="!h-9 !px-3 !text-sm font-medium" onClick={handleConfirm} disabled={!hasChanges}>
            {t('confirmText', 'Confirmar')}
          </Button>
        </div>
      </div>
    </div>
  );
};
